import type { ApolloError } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';

import type {
  Asset2ReviewStatus,
  CalculateCreativeDataAdDetailsInput,
  CalculatedCreativeDataForAdByAdDetailsQuery,
  CalculatedCreativeDataForAdByAdDetailsQueryVariables,
} from '../../../apis/graphql';
import { GET_CALCULATED_CREATIVE_DATA_FOR_AD_BY_AD_DETAILS } from '../../../apis/graphql/common/queries/ad/getCalculatedCreativeDataForAdByAdDetails';
import { showFailure } from '../../utils';

type CalculatedCreativeDataMappingEntry = CalculatedCreativeDataForAdByAdDetailsQuery['getCalculatedCreativeDataForAdsByAdDetails'][number];

export type UseFetchCalculatedCreativeDataProps = {
  skip?: boolean;
  adTargetingTermValues?: CalculateCreativeDataAdDetailsInput[];
};

export type UseFetchCalculatedCreativeData = {
  creativeStatusMap: Record<string, Asset2ReviewStatus | null>;
  loading: boolean;
  error?: ApolloError;
};

export const FAILED_TO_FETCH_CREATIVE_DATA_MESSAGE = 'Failed to fetch creative data';

export const mapCreativeStatusesByAdId = (
  entries?: CalculatedCreativeDataMappingEntry[]
): Record<string, Asset2ReviewStatus | null> => {
  return Object.fromEntries((entries ?? []).map(({ adId, calculatedData }) => [adId, calculatedData?.status ?? null]));
};

export const useFetchCalculatedCreativeData = ({
  skip,
  adTargetingTermValues,
}: UseFetchCalculatedCreativeDataProps): UseFetchCalculatedCreativeData => {
  const { data: dataByDetails, loading, error } = useQuery<
    CalculatedCreativeDataForAdByAdDetailsQuery,
    CalculatedCreativeDataForAdByAdDetailsQueryVariables
  >(GET_CALCULATED_CREATIVE_DATA_FOR_AD_BY_AD_DETAILS, {
    variables: { adDetails: adTargetingTermValues ?? [] },
    skip: skip,
    fetchPolicy: 'no-cache',
  });

  const data = dataByDetails?.getCalculatedCreativeDataForAdsByAdDetails;

  useEffect(() => {
    if (error) {
      showFailure({
        message: FAILED_TO_FETCH_CREATIVE_DATA_MESSAGE,
        description: error.message,
      });
    }
  }, [error]);

  const creativeStatusMap = useMemo(() => mapCreativeStatusesByAdId(data), [data]);

  return {
    creativeStatusMap,
    loading,
    error,
  };
};
